import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeviceInfo, DeviceInfoService, MessageService, rangeCreator, StorageService } from '@client-monorepo/common/utilities';
import { OverlayManagerService } from '@client-monorepo/common/ui-components';
import { convertNonEnglishDigits } from '@digipay/strings';
import { ShahkarService } from '@client-monorepo/common/shahkar';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { AuthResponse, AuthService, TacService } from '@client-monorepo/common/user';
import { UserBlockErrorComponent } from '../user-block-error/user-block-error.component';
import { generateRateLimitMessage } from '../../data-access/utils/rate-limit-message-generator';
import { PinStatus } from '../../data-access/models/pin-status.enum';
import { PinComponentStateEnum } from '../../data-access/models/pin-component-state.enum';
import { PinConfigInterface } from '../../data-access/models/pin-config.interface';
import { AnimationLoader, LottieComponent, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web/build/player/lottie_light';
import { EnterPasswordEnum } from '../../data-access/models/enter-password.enum';
import { NgxHybridService } from '@digipay/ngx-hybrid-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'common-pin-user-pin',
  standalone: true,
  imports: [CommonModule, FormsModule, LottieComponent],
  templateUrl: './pin.component.html',
  styleUrl: './pin.component.scss',
  providers: [
    AnimationLoader,
    provideLottieOptions({
      player: () => player,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PinComponent implements OnInit {
  pinConfig = input<PinConfigInterface>({} as PinConfigInterface);
  pinInputLength = signal(['', '', '', '']);
  pinDivs = signal(['-', '-', '-', '-']);
  hiddenInputValue = signal('');
  isFocuse = signal<boolean>(false);
  newPasswordType = computed(() => this.pinConfig().newPasswordType ?? false);
  type = computed(() => this.pinConfig().type ?? false); // if false pin opened in bottom sheet
  forgotPassword = computed(() => this.pinConfig().forgotPassword ?? false);
  phoneNumber = computed(() => this.pinConfig().phoneNumber ?? '');
  features = computed(() => this.pinConfig().features ?? []);
  requestHeaders = computed(() => this.pinConfig().requestHeaders ?? undefined);
  hasBackground = computed(() => this.pinConfig().hasBackground ?? true);
  payType = computed(() => this.pinConfig().isPayType ?? false);
  enteredPassword = signal('');
  confirmPassword = signal('');
  state = signal(PinComponentStateEnum.OLD);
  enterPasswordState = signal(EnterPasswordEnum.ENTER);
  showBiometric = input<boolean>(true);
  title = computed(() => {
    switch (this.state()) {
      case PinComponentStateEnum.OLD:
        return 'رمز عبور خود را وارد کنید.';
      case PinComponentStateEnum.NEW:
        return 'رمز عبور جدید خود را وارد کنید.';
      case PinComponentStateEnum.CONFIRM:
        return 'رمز عبور جدید خود را مجددا وارد کنید.';
      case PinComponentStateEnum.PAY:
        return 'برای تایید پرداخت رمز خود را وارد کنید.';
    }
  });
  pinAnimation = computed(() => {
    switch (this.enterPasswordState()) {
      case EnterPasswordEnum.ENTER:
        return '/assets/pin/enter-password.json';
      case EnterPasswordEnum.CORRECT:
        return '/assets/pin/correct-password.json';
      case EnterPasswordEnum.WRONG:
        return '/assets/pin/wrong-password.json';
    }
  });
  pinAutocompleteAttribute = computed(() => {
    const state = this.state();
    return state === PinComponentStateEnum.NEW || state === PinComponentStateEnum.CONFIRM
      ? 'new-password'
      : this.storageService.isSetPassword()
        ? 'current-password'
        : 'new-password';
  });
  hint = signal('');

  pin = signal(['', '', '', '']);
  textPin = signal('');
  activeStep = signal(0);
  hasBiometric = signal(false);
  device!: DeviceInfo;

  callbackFunction = output<PinStatus>();
  pinOutput = output<string>();
  authService = inject(AuthService);
  deviceInfoService = inject(DeviceInfoService);
  storageService = inject(StorageService);
  bottomSheetService = inject(NgxBottomSheetService);
  shahkarService = inject(ShahkarService);
  overlayManagerService = inject(OverlayManagerService);
  messageService = inject(MessageService);
  private tacService = inject(TacService);
  private ngxHybridService = inject(NgxHybridService);
  private untilDestroy = inject(DestroyRef);
  submitting = false;
  ngOnInit() {
    this.focusOnElementWithId('password');
    if (this.newPasswordType()) {
      this.state.set(PinComponentStateEnum.NEW);
      this.hint.set('ارقام تکرارشونده و یا متوالی وارد نکنید.');
    }
    if (this.payType()) {
      this.state.set(PinComponentStateEnum.PAY);
    }
    this.checkHasBiometric();
  }
  private checkHasBiometric(): void {
    if (this.storageService.hasBiometric() && this.showBiometric()) {
      this.hasBiometric.set(true);
      this.enterPinWithBiometric();
    }
  }
  hiddenInputChange(value: string): void {
    value = convertNonEnglishDigits(value).replace(/[^0-9]/g, '');
    this.hiddenInputValue.set(value);
    const updatedValue = Array(4).fill('');
    value
      .split('')
      .slice(0, 4)
      .forEach((char, index) => {
        updatedValue[index] = char;
      });
    this.pinInputLength.set(updatedValue);
    const pinDivValue = value.split('').map((char, index) => {
      return index < 4 ? char : '-';
    });
    this.pinDivs.set(pinDivValue);

    while (this.pinDivs().length < 4) {
      this.pinDivs().push('-');
    }
    const isFilled = updatedValue.every((char) => char !== '');
    if (isFilled) {
      this.pinComplete();
      return;
    }
  }
  private focusOnElementWithId(id: string): void {
    const elem = document.getElementById(id);
    if (elem) {
      elem.focus();
    }
  }
  inputKeyDown(event: KeyboardEvent) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.resetPin();
      return;
    }
  }
  pinComplete(): void {
    this.textPin.set(this.pinInputLength().join(''));
    switch (this.state()) {
      case PinComponentStateEnum.PAY:
      case PinComponentStateEnum.OLD:
        this.enteredPassword.set(this.textPin());
        this.checkPin().then();
        break;
      case PinComponentStateEnum.NEW:
        if (this.isInvalidPattern(this.textPin())) {
          const errorMessage = 'رمز عبور انتخابی ساده و قابل حدس است. لطفاً رمزی امن‌تر وارد کنید.';
          this.messageService.showErrorMessage(errorMessage);
          this.resetPin();
          return;
        }
        this.hint.set('');
        this.enteredPassword.set(this.textPin());
        this.state.set(PinComponentStateEnum.CONFIRM);
        this.resetPin();
        break;
      case PinComponentStateEnum.CONFIRM:
        this.confirmPassword.set(this.textPin());
        this.checkPasswordEquality();
        break;
    }
  }
  private isInvalidPattern(input: string): boolean {
    const firstDigit = input[0];
    let allSame = true;
    let isAscending = true;
    let isDescending = true;

    for (let i = 1; i < input.length; i++) {
      const curr = input[i];
      const prev = input[i - 1];

      if (curr !== firstDigit) allSame = false;

      const diff = curr.charCodeAt(0) - prev.charCodeAt(0);

      if (diff !== 1) isAscending = false;

      if (diff !== -1) isDescending = false;

      if (!allSame && !isAscending && !isDescending) return false;
    }
    return allSame || isAscending || isDescending;
  }
  async checkPin(): Promise<void> {
    this.submitting = true;
    const device = await this.deviceInfoService.getDeviceInfo();
    this.authService
      .login(
        {
          username: this.storageService.getUserId(),
          password: this.textPin(),
          features: this.features(),
          device: device,
        },
        this.requestHeaders(),
      )
      .subscribe({
        next: (res: AuthResponse) => {
          this.storageService.updateAuth(res);
          if (this.pinConfig().isCallTac) {
            this.tacService.getTac();
          }
          if (!this.storageService.isSetPassword()) {
            this.storageService.setPassword();
          }
          if (this.pinConfig().isBiometricType) {
            this.setBiometric();
            return;
          }
          this.shahkarService.handleShahkarOverlay({ cancelable: true }).then((result) => {
            if (result) {
              this.successEnterPin();
            } else {
              this.resetPin();
            }
          });
          this.submitting = false;
        },
        error: (error) => {
          const { attemptsResetTime, remainingAttempts } = error.error;
          if (error?.error?.result?.status === 1130) {
            this.handleBlockedStatus(attemptsResetTime);
          } else {
            this.handleCheckPinErrors(remainingAttempts, attemptsResetTime);
          }
          this.handleIncorrectPassword();
          this.submitting = false;
        },
      });
  }
  setBiometric(): void {
    this.ngxHybridService
      .authenticateBiometric()
      .pipe(takeUntilDestroyed(this.untilDestroy))
      .subscribe({
        next: (result) => {
          if (result) {
            this.ngxHybridService.setPin(this.textPin()).then((result: boolean) => {
              if (result) {
                this.handleCallbackFunction(PinStatus.SUCCESS);
                this.storageService.setHasBiometric();
              }
            });
          }
        },
        error: () => {
          this.handleCallbackFunction(PinStatus.FAILED);
        },
      });
  }
  enterPinWithBiometric(): void {
    if (!this.submitting) {
      this.ngxHybridService
        .authenticateBiometric()
        .pipe(takeUntilDestroyed(this.untilDestroy))
        .subscribe({
          next: (result: boolean) => {
            if (result) {
              this.ngxHybridService.getPin().then((pin: string) => {
                if (pin) {
                  this.hiddenInputChange(pin);
                }
              });
            }
          },
        });
    }
  }
  handleIncorrectPassword(): void {
    const errorTimeCount = this.hasBackground() ? 1500 : 1000;
    this.enterPasswordState.set(EnterPasswordEnum.WRONG);
    this.hint.set('رمز عبور اشتباه است!');
    setTimeout(() => {
      this.enterPasswordState.set(EnterPasswordEnum.ENTER);
      this.hint.set('');
      this.resetPin();
    }, errorTimeCount);
  }
  handleBlockedStatus(attemptsResetTime: number) {
    this.overlayManagerService.displayOverlay(UserBlockErrorComponent, { attemptsResetTime }, { type: 'error' }).then(() => {
      this.handleCallbackFunction(PinStatus.BLOCKED);
    });
  }

  handleCheckPinErrors(remainingAttempts: number, attemptsResetTime: number) {
    const errorMessage = generateRateLimitMessage(remainingAttempts, attemptsResetTime);
    if (remainingAttempts < 2) {
      this.messageService.showErrorMessage(errorMessage);
    } else if (remainingAttempts < 4) {
      this.messageService.showWarningMessage(errorMessage);
    }
  }

  successEnterPin(): void {
    if (!this.hasBackground()) {
      this.handleCallbackFunction(PinStatus.SUCCESS);
      return;
    }
    this.enterPasswordState.set(EnterPasswordEnum.CORRECT);
    setTimeout(() => {
      this.handleCallbackFunction(PinStatus.SUCCESS);
    }, 1400);
  }

  checkPasswordEquality(): void {
    if (this.confirmPassword() !== this.enteredPassword()) {
      this.hint.set('رمزهای ورودی مطابقت ندارند.مجددا تلاش کنید.');
      this.state.set(PinComponentStateEnum.NEW);
      this.resetPin();
    } else if (this.forgotPassword()) {
      this.pinOutput.emit(this.confirmPassword());
    } else {
      this.authService.setUserPassword(this.textPin()).subscribe({
        next: () => {
          this.resetBiometricAfterPinChange(this.textPin());
          this.successEnterPin();
        },
        error: () => {
          this.messageService.showErrorMessage('مشکلی بوجود آمده است! .مجددا تلاش کنید.');
          this.state.set(PinComponentStateEnum.NEW);
          this.resetPin();
        },
      });
    }
  }
  resetPin(): void {
    this.pinInputLength.set(['', '', '', '']);
    this.pinDivs.set(['-', '-', '-', '-']);
    this.hiddenInputValue.set('');
    this.focusOnElementWithId('password');
  }
  onDivClicked(): void {
    this.focusOnElementWithId('password');
  }
  handleCallbackFunction(status: PinStatus): void {
    if (this.type()) {
      this.callbackFunction.emit(status);
    } else {
      this.bottomSheetService.outputData.set({ res: status });
      this.bottomSheetService.closeBottomSheet();
    }
  }

  resetBiometricAfterPinChange(pin: string): void {
    if (this.storageService.hasBiometric()) {
      this.ngxHybridService.checkBiometricAvailability().then((result) => {
        if (result) {
          this.ngxHybridService.setPin(pin).then();
        }
      });
    }
  }

  protected readonly rangeCreator = rangeCreator;
  protected readonly EnterPasswordEnum = EnterPasswordEnum;
}
